import { cleaningRoomRepository, cleaningTaskRepository } from '../db/database';
import type {
  CleaningRoom,
  CleaningRoomId,
  CleaningTask,
  IconName,
} from '../models/types';
import { syncAutomaticCleaningLog } from '../services/cleaning';
import { queueNotificationSync } from '../services/notificationSync';
import { todayIso } from '../utils/date';
import { setDelegatedClick } from '../ui/delegatedClick';
import { renderIcon } from '../ui/icons';
import { attachIconPicker, renderIconPicker, ROOM_ICON_CHOICES } from '../ui/iconPicker';
import type { RouteId } from '../ui/tabBar';

/**
 * Temizlik yönetimi — oda ve görev ekle / düzenle / sil.
 *
 * Temizlik ekranındaki sabit "Görevleri Düzenle" butonundan açılır.
 * Odalar ve görevler tamamen kullanıcı tanımlıdır (§7.5); sabit bir
 * liste yoktur.
 */

/** Görev döngüsü için üst sınır — bir yıldan uzun bir döngü anlamsız. */
const MAX_CYCLE_DAYS = 365;

type EditMode =
  | { type: 'list' }
  /** `room` yoksa yeni oda ekleniyor. */
  | { type: 'roomForm'; room: CleaningRoom | undefined }
  /**
   * `task` yoksa yeni görev ekleniyor; `roomId` o durumda hangi odaya
   * ekleneceğini önceden seçili getirir.
   */
  | { type: 'taskForm'; task: CleaningTask | undefined; roomId: CleaningRoomId | undefined };

/** Silme onayı bekleyen kayıt. */
type PendingDelete = { kind: 'room' | 'task'; id: string } | null;

let mode: EditMode = { type: 'list' };
let pendingDelete: PendingDelete = null;

function escapeHtml(text: string): string {
  const element = document.createElement('span');
  element.textContent = text;
  return element.innerHTML;
}

/* ==================================================================
   Liste
   ================================================================== */

function renderTaskRow(task: CleaningTask): string {
  return `
    <li class="edit-task">
      <button type="button" class="edit-task__main" data-edit-task="${task.id}">
        <span class="edit-task__name">${escapeHtml(task.name)}</span>
        <span class="edit-task__cycle">${task.cycleDays} günde bir</span>
      </button>
      <button type="button" class="manage-row__delete" data-delete-task="${task.id}"
              aria-label="${escapeHtml(task.name)} görevini sil">Sil</button>
    </li>
  `;
}

function renderRoomBlock(room: CleaningRoom, tasks: CleaningTask[]): string {
  const taskList = tasks.length
    ? `<ul class="edit-task-list" role="list">${tasks.map(renderTaskRow).join('')}</ul>`
    : '<p class="edit-room__empty">Bu odada henüz görev yok.</p>';

  return `
    <section class="edit-room">
      <header class="edit-room__header">
        <span class="room-card__icon">${renderIcon(room.icon, { size: 16 })}</span>
        <h2 class="edit-room__name">${escapeHtml(room.name)}</h2>
        <button type="button" class="edit-room__action" data-edit-room="${room.id}">Düzenle</button>
        <button type="button" class="manage-row__delete" data-delete-room="${room.id}"
                aria-label="${escapeHtml(room.name)} odasını sil">Sil</button>
      </header>
      ${taskList}
      <button type="button" class="edit-room__add" data-add-task="${room.id}">+ Görev ekle</button>
    </section>
  `;
}

/**
 * Silme onayı.
 *
 * Oda silmek içindeki tüm görevleri de siler; bu yüzden kaç görevin
 * gideceğini açıkça söylüyoruz — kullanıcı ne kaybedeceğini bilmeli.
 */
function renderDeleteConfirm(
  rooms: CleaningRoom[],
  tasks: CleaningTask[],
): string {
  if (!pendingDelete) return '';

  let title: string;
  let text: string;

  if (pendingDelete.kind === 'room') {
    const room = rooms.find((candidate) => candidate.id === pendingDelete?.id);
    if (!room) return '';
    const taskCount = tasks.filter((task) => task.roomId === room.id).length;
    title = `“${escapeHtml(room.name)}” odası silinsin mi?`;
    text =
      taskCount > 0
        ? `Odanın içindeki ${taskCount} görev de silinecek. Bu işlem geri alınamaz.`
        : 'Bu işlem geri alınamaz.';
  } else {
    const task = tasks.find((candidate) => candidate.id === pendingDelete?.id);
    if (!task) return '';
    title = `“${escapeHtml(task.name)}” görevi silinsin mi?`;
    text = 'Bu işlem geri alınamaz.';
  }

  return `
    <div class="challenge-confirm">
      <p class="challenge-confirm__title">${title}</p>
      <p class="challenge-confirm__text">${text}</p>
      <div class="challenge-confirm__actions">
        <button type="button" class="challenge-confirm__no" data-abort-delete>Vazgeç</button>
        <button type="button" class="challenge-confirm__yes" data-confirm-delete>Evet, sil</button>
      </div>
    </div>
  `;
}

function renderList(rooms: CleaningRoom[], tasks: CleaningTask[]): string {
  const blocks = rooms.length
    ? rooms
        .map((room) =>
          renderRoomBlock(
            room,
            tasks.filter((task) => task.roomId === room.id),
          ),
        )
        .join('')
    : `<div class="placeholder">
         <p class="placeholder__title">Henüz oda yok</p>
         <p class="placeholder__hint">Aşağıdaki butondan ilk odanı ekle.</p>
       </div>`;

  return `
    <div class="cleaning-page">
      <header class="detail-header">
        <button type="button" class="back-button" data-back aria-label="Temizliğe dön">
          <svg width="8" height="14" viewBox="0 0 8 14" aria-hidden="true" focusable="false">
            <path d="M7 1L1 7l6 6" stroke="currentColor" stroke-width="2" fill="none"
                  stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        <h1 class="detail-header__title">Görevleri Düzenle</h1>
      </header>
      ${blocks}
      ${renderDeleteConfirm(rooms, tasks)}
      <div class="fab-bar">
        <button type="button" class="fab" data-add-room>
          ${renderIcon('home', { size: 17, color: 'white' })}
          Yeni Oda
        </button>
      </div>
    </div>
  `;
}

/* ==================================================================
   Formlar
   ================================================================== */

function renderBackHeader(title: string): string {
  return `
    <header class="detail-header">
      <button type="button" class="back-button" data-back-to-list aria-label="Listeye dön">
        <svg width="8" height="14" viewBox="0 0 8 14" aria-hidden="true" focusable="false">
          <path d="M7 1L1 7l6 6" stroke="currentColor" stroke-width="2" fill="none"
                stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      <h1 class="detail-header__title">${title}</h1>
    </header>
  `;
}

function renderRoomForm(room: CleaningRoom | undefined): string {
  const isNew = room === undefined;
  const icon: IconName = room?.icon ?? 'home';

  return `
    ${renderBackHeader(isNew ? 'Yeni Oda' : 'Odayı Düzenle')}
    <form class="challenge-form" data-room-form novalidate>
      <label class="field">
        <span class="field__label">Oda adı</span>
        <input class="field__input" type="text" name="name" maxlength="30"
               value="${escapeHtml(room?.name ?? '')}" placeholder="Örn. Mutfak" />
      </label>
      <div class="field">
        <span class="field__label">İkon</span>
        ${renderIconPicker(icon, ROOM_ICON_CHOICES)}
      </div>
      <p class="challenge-form__error" data-form-error hidden></p>
      <button type="submit" class="challenge-form__submit">${isNew ? 'Ekle' : 'Kaydet'}</button>
    </form>
  `;
}

function renderTaskForm(
  task: CleaningTask | undefined,
  rooms: CleaningRoom[],
  preselectedRoomId: CleaningRoomId | undefined,
): string {
  const isNew = task === undefined;
  const selectedRoomId = task?.roomId ?? preselectedRoomId ?? rooms[0]?.id ?? '';

  const roomOptions = rooms
    .map(
      (room) => `
        <option value="${room.id}" ${room.id === selectedRoomId ? 'selected' : ''}>
          ${escapeHtml(room.name)}
        </option>
      `,
    )
    .join('');

  return `
    ${renderBackHeader(isNew ? 'Yeni Görev' : 'Görevi Düzenle')}
    <form class="challenge-form" data-task-form novalidate>
      <label class="field">
        <span class="field__label">Oda</span>
        <select class="field__input" name="roomId">${roomOptions}</select>
      </label>
      <label class="field">
        <span class="field__label">Görev adı</span>
        <input class="field__input" type="text" name="name" maxlength="40"
               value="${escapeHtml(task?.name ?? '')}" placeholder="Örn. Bulaşık yıkama" />
      </label>
      <label class="field">
        <span class="field__label">Döngü süresi (gün)</span>
        <input class="field__input" type="text" inputmode="numeric" name="cycleDays"
               value="${task?.cycleDays ?? 7}" />
      </label>
      <p class="challenge-form__error" data-form-error hidden></p>
      <button type="submit" class="challenge-form__submit">${isNew ? 'Ekle' : 'Kaydet'}</button>
    </form>
  `;
}

/* ==================================================================
   Kaydetme
   ================================================================== */

async function saveRoom(existing: CleaningRoom | undefined, name: string, icon: IconName): Promise<void> {
  if (existing) {
    await cleaningRoomRepository.upsert({ ...existing, name, icon });
    return;
  }

  const rooms = await cleaningRoomRepository.list();
  await cleaningRoomRepository.upsert({
    id: crypto.randomUUID(),
    name,
    icon,
    sortOrder: rooms.length,
  });
}

async function saveTask(
  existing: CleaningTask | undefined,
  values: { roomId: CleaningRoomId; name: string; cycleDays: number },
): Promise<void> {
  if (existing) {
    // Döngü süresi değişirse bir sonraki yapılma tarihi kendiliğinden
    // kayar; son tamamlanma tarihine dokunmuyoruz.
    await cleaningTaskRepository.upsert({ ...existing, ...values });
    return;
  }

  const tasks = await cleaningTaskRepository.listByRoom(values.roomId);
  await cleaningTaskRepository.upsert({
    id: crypto.randomUUID(),
    ...values,
    // Yeni görev hiç yapılmamış sayılır; hemen "Bekliyor" durumunda
    // başlar (§7.5).
    lastCompletedDate: null,
    previousCompletedDate: null,
    sortOrder: tasks.length,
  });
}

/* ==================================================================
   Çizim
   ================================================================== */

/**
 * Her değişiklikten sonra otomatik "Temizlik" alışkanlığının bugünkü
 * kaydını tazeler.
 *
 * Gerekli çünkü görev eklemek/silmek §7.5'teki "sırası gelmiş görev
 * var mı?" sorusunun cevabını anında değiştirir: son bekleyen görevi
 * silen kullanıcı, Anasayfa'ya döndüğünde rozeti yeşil görmeli.
 */
async function syncAndRedraw(redraw: () => Promise<void>): Promise<void> {
  await syncAutomaticCleaningLog(todayIso());
  // §7.8.3 — Görev listesi değişmiş olabilir (eklendi/silindi/döngüsü
  // değişti). Sunucudaki kopya güncellenmezse silinmiş bir görev için
  // hatırlatma gelmeye devam ederdi.
  queueNotificationSync();
  await redraw();
}

async function draw(
  container: HTMLElement,
  navigate: (route: RouteId) => Promise<void>,
): Promise<void> {
  const [rooms, tasks] = await Promise.all([
    cleaningRoomRepository.list(),
    cleaningTaskRepository.list(),
  ]);
  const redraw = (): Promise<void> => draw(container, navigate);

  // Modül düzeyindeki `mode` dallanma sonrası daraltılamadığı için
  // yerel bir kopyaya alıyoruz.
  const current = mode;

  if (current.type === 'list') {
    container.innerHTML = renderList(rooms, tasks);
    attachListHandlers(container, navigate, redraw);
    return;
  }

  if (current.type === 'roomForm') {
    container.innerHTML = renderRoomForm(current.room);
    attachRoomFormHandlers(container, current.room, redraw);
    return;
  }

  container.innerHTML = renderTaskForm(current.task, rooms, current.roomId);
  attachTaskFormHandlers(container, current.task, redraw);
}

/* ==================================================================
   Olaylar
   ================================================================== */

function attachListHandlers(
  container: HTMLElement,
  navigate: (route: RouteId) => Promise<void>,
  redraw: () => Promise<void>,
): void {
  // Liste her işlemden sonra kendini yeniden çiziyor. `setDelegatedClick`
  // bir önceki çizimin dinleyicisini kaldırır — aksi halde dinleyiciler
  // üst üste birikir ve tek dokunuş birden çok kez işlenirdi
  // (örn. bir oda silinirken onay iki kez tetiklenirdi).
  setDelegatedClick('cleaningEdit', container, (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('[data-back]')) {
      void navigate('cleaning');
      return;
    }

    if (target.closest('[data-add-room]')) {
      mode = { type: 'roomForm', room: undefined };
      void redraw();
      return;
    }

    const addTask = target.closest<HTMLElement>('[data-add-task]');
    if (addTask) {
      mode = { type: 'taskForm', task: undefined, roomId: addTask.dataset['addTask'] };
      void redraw();
      return;
    }

    const editRoom = target.closest<HTMLElement>('[data-edit-room]');
    if (editRoom) {
      void (async () => {
        const rooms = await cleaningRoomRepository.list();
        mode = {
          type: 'roomForm',
          room: rooms.find((room) => room.id === editRoom.dataset['editRoom']),
        };
        await redraw();
      })();
      return;
    }

    const editTask = target.closest<HTMLElement>('[data-edit-task]');
    if (editTask) {
      void (async () => {
        const tasks = await cleaningTaskRepository.list();
        const task = tasks.find((candidate) => candidate.id === editTask.dataset['editTask']);
        mode = { type: 'taskForm', task, roomId: task?.roomId };
        await redraw();
      })();
      return;
    }

    const deleteRoom = target.closest<HTMLElement>('[data-delete-room]');
    if (deleteRoom) {
      pendingDelete = { kind: 'room', id: deleteRoom.dataset['deleteRoom'] ?? '' };
      void redraw();
      return;
    }

    const deleteTask = target.closest<HTMLElement>('[data-delete-task]');
    if (deleteTask) {
      pendingDelete = { kind: 'task', id: deleteTask.dataset['deleteTask'] ?? '' };
      void redraw();
      return;
    }

    if (target.closest('[data-abort-delete]')) {
      pendingDelete = null;
      void redraw();
      return;
    }

    if (target.closest('[data-confirm-delete]')) {
      const request = pendingDelete;
      if (!request) return;
      void (async () => {
        // Oda silmek içindeki görevleri de siler (repository hallediyor).
        // Temizlik verisi puan defterine doğrudan girmediği için burada
        // gerçek silme güvenli — §7.2'nin koruduğu geçmiş etkilenmez.
        if (request.kind === 'room') {
          await cleaningRoomRepository.remove(request.id);
        } else {
          await cleaningTaskRepository.remove(request.id);
        }
        pendingDelete = null;
        await syncAndRedraw(redraw);
      })();
    }
  });
}

function attachRoomFormHandlers(
  container: HTMLElement,
  existing: CleaningRoom | undefined,
  redraw: () => Promise<void>,
): void {
  const form = container.querySelector<HTMLFormElement>('[data-room-form]');
  if (!form) return;

  attachIconPicker(container);

  container.querySelector('[data-back-to-list]')?.addEventListener('click', () => {
    mode = { type: 'list' };
    void redraw();
  });

  const error = container.querySelector<HTMLElement>('[data-form-error]');

  form.addEventListener('submit', (event) => {
    event.preventDefault();

    const name = (form.elements.namedItem('name') as HTMLInputElement).value.trim();
    const icon = (form.elements.namedItem('icon') as HTMLInputElement).value as IconName;

    if (!name) {
      if (error) {
        error.textContent = 'Odaya bir ad ver.';
        error.hidden = false;
      }
      return;
    }

    void (async () => {
      await saveRoom(existing, name, icon);
      mode = { type: 'list' };
      await syncAndRedraw(redraw);
    })();
  });
}

function attachTaskFormHandlers(
  container: HTMLElement,
  existing: CleaningTask | undefined,
  redraw: () => Promise<void>,
): void {
  const form = container.querySelector<HTMLFormElement>('[data-task-form]');
  if (!form) return;

  container.querySelector('[data-back-to-list]')?.addEventListener('click', () => {
    mode = { type: 'list' };
    void redraw();
  });

  const error = container.querySelector<HTMLElement>('[data-form-error]');

  form.addEventListener('submit', (event) => {
    event.preventDefault();

    const roomId = (form.elements.namedItem('roomId') as HTMLSelectElement).value;
    const name = (form.elements.namedItem('name') as HTMLInputElement).value.trim();
    const cycleDays = Number(
      (form.elements.namedItem('cycleDays') as HTMLInputElement).value.trim(),
    );

    if (!roomId) {
      if (error) {
        error.textContent = 'Önce bir oda ekle.';
        error.hidden = false;
      }
      return;
    }
    if (!name) {
      if (error) {
        error.textContent = 'Göreve bir ad ver.';
        error.hidden = false;
      }
      return;
    }
    // Döngü tam gün sayısı olmalı: yarım günlük bir döngünün
    // karşılığı yok, tüm hesap gün bazında çalışıyor.
    if (!Number.isInteger(cycleDays) || cycleDays < 1 || cycleDays > MAX_CYCLE_DAYS) {
      if (error) {
        error.textContent = `Döngü 1 ile ${MAX_CYCLE_DAYS} arasında bir tam sayı olmalı.`;
        error.hidden = false;
      }
      return;
    }

    void (async () => {
      await saveTask(existing, { roomId, name, cycleDays });
      mode = { type: 'list' };
      await syncAndRedraw(redraw);
    })();
  });
}

/* ==================================================================
   Giriş noktası
   ================================================================== */

export async function renderCleaningEditPage(
  container: HTMLElement,
  navigate: (route: RouteId) => Promise<void>,
): Promise<void> {
  // Ekrana her girişte listeden başla — yarım kalmış bir formla
  // karşılaşılmasın.
  mode = { type: 'list' };
  pendingDelete = null;
  await draw(container, navigate);
}
